import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { ChevronDown, Plus, Search } from "lucide-react";
import type { AccountBalance } from "@/gen/omnios/v1/accounting_pb";
import {
	useAccountsByPrefix,
	useCreateReconSession,
	useListAccounts,
} from "@/hooks/use-accounting";

import type { ColDef, ValueFormatterParams } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { useGridLayout } from "@/lib/use-grid-layout";
import { Timestamp } from "@bufbuild/protobuf";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/accounting/bank")({
	component: BankAccountsPage,
});

function formatAmount(value: number): string {
	return new Intl.NumberFormat("ru-RU", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value);
}

function BankAccountsPage() {
	const { t } = useTranslation("accounting");
	useBreadcrumbs([{ label: t("nav_bank") }]);
	const navigate = useNavigate();
	const gridRef = useRef<AgGridReact>(null);
	const { onGridReady, onStateChanged } = useGridLayout("bank");
	const { lines: bankLines, isLoading } = useAccountsByPrefix("51");
	const { data: accountsData } = useListAccounts();
	const nameMap = useMemo(() => {
		const map = new Map<string, string>();
		for (const a of accountsData?.accounts ?? []) map.set(a.code, a.name);
		return map;
	}, [accountsData]);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [selectedAccount, setSelectedAccount] = useState("");
	const [statementDate, setStatementDate] = useState("");
	const createRecon = useCreateReconSession();

	const handleCreateRecon = () => {
		if (!selectedAccount || !statementDate) return;
		createRecon.mutate(
			{
				bankAccountId: selectedAccount,
				statementDate: Timestamp.fromDate(new Date(statementDate)),
			},
			{
				onSuccess: (session) => {
					setDialogOpen(false);
					setSelectedAccount("");
					setStatementDate("");
					navigate({
						to: "/accounting/bank/$id",
						params: { id: session.id },
					});
				},
			},
		);
	};

	const columnDefs = useMemo<ColDef<AccountBalance>[]>(
		() => [
			{
				field: "accountCode",
				headerName: t("account_code"),
				width: 120,
				pinned: "left" as const,
				cellClass: "font-mono",
			},
			{
				headerName: t("account_name"),
				flex: 1,
				minWidth: 200,
				valueGetter: (p) => nameMap.get(p.data?.accountCode ?? "") ?? "",
			},
			{
				field: "totalDebits",
				headerName: t("total_debits"),
				width: 140,
				type: "rightAligned",
				cellClass: "font-tabular",
				valueGetter: (p) => Number.parseFloat(p.data?.totalDebits || "0"),
				valueFormatter: (p: ValueFormatterParams) =>
					p.value ? formatAmount(p.value) : "\u2014",
			},
			{
				field: "totalCredits",
				headerName: t("total_credits"),
				width: 140,
				type: "rightAligned",
				cellClass: "font-tabular",
				valueGetter: (p) => Number.parseFloat(p.data?.totalCredits || "0"),
				valueFormatter: (p: ValueFormatterParams) =>
					p.value ? formatAmount(p.value) : "\u2014",
			},
			{
				field: "net",
				headerName: t("account_balance"),
				width: 160,
				type: "rightAligned",
				cellClass: "font-tabular font-medium",
				valueGetter: (p) => Number.parseFloat(p.data?.net || "0"),
				valueFormatter: (p: ValueFormatterParams) => formatAmount(p.value ?? 0),
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			filter: true,
			filterParams: {
				buttons: ["apply", "reset"],
				closeOnApply: true,
			},
		}),
		[],
	);

	const onRowClicked = useCallback(
		(e: { data: AccountBalance | undefined }) => {
			if (e.data) {
				navigate({
					to: "/accounting/bank/$id",
					params: { id: e.data.accountCode },
				});
			}
		},
		[navigate],
	);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content: t("row_count", { count: bankLines.length }),
				align: "left" as const,
			},
		],
		[bankLines.length, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption(
								"quickFilterText",
								e.target.value,
							)
						}
						className="pl-9 h-9"
					/>
				</div>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="outline" size="sm" className="h-9">
							{t("toolbar_export")}
							<ChevronDown className="h-3.5 w-3.5 ml-1 opacity-50" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem
							onClick={() => gridRef.current?.api?.exportDataAsCsv()}
						>
							CSV
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
				<Button size="sm" className="h-9" onClick={() => setDialogOpen(true)}>
					<Plus className="h-4 w-4 mr-1" />
					{t("recon_create")}
				</Button>
			</div>
			<div className="flex-1 ag-theme-quartz" style={{ width: "100%" }}>
				<AgGridReact<AccountBalance>
					ref={gridRef}
					rowData={bankLines}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					onGridReady={onGridReady}
					onSortChanged={onStateChanged}
					onFilterChanged={onStateChanged}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					rowHeight={28}
					headerHeight={32}
					animateRows={false}
					getRowId={(p) => p.data.accountCode}
					loading={isLoading}
					onRowClicked={onRowClicked}
					rowClass="cursor-pointer"
					overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("empty_bank_title", { defaultValue: "Нет банковских счетов" })}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("empty_bank_desc", { defaultValue: "Добавьте счёт с кодом 51.* в план счетов" })}</p></div>`}
				/>
			</div>

			{/* Create Reconciliation Dialog */}
			<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("recon_create")}</DialogTitle>
						<DialogDescription>{t("recon_bank_account")}</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-2">
						<div className="space-y-2">
							<Label>{t("recon_bank_account")}</Label>
							<Select
								value={selectedAccount}
								onValueChange={setSelectedAccount}
							>
								<SelectTrigger>
									<SelectValue placeholder={t("account_picker_placeholder")} />
								</SelectTrigger>
								<SelectContent>
									{bankLines.map((line) => (
										<SelectItem key={line.accountCode} value={line.accountCode}>
											{line.accountCode}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label>{t("recon_statement_date")}</Label>
							<Input
								type="date"
								value={statementDate}
								onChange={(e) => setStatementDate(e.target.value)}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button
							onClick={handleCreateRecon}
							disabled={
								!selectedAccount || !statementDate || createRecon.isPending
							}
						>
							{t("recon_create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
